import React, { useEffect, useState } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Candidate } from '../types/hr.types';
import { useToast } from './ui/Toast';
import { CheckCircle2, CalendarPlus } from 'lucide-react';
interface HireCandidateModalProps {
  candidate: Candidate | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (candidate: Candidate, payload: {
    startDate: string;
    offerDecisionDate: string;
  }) => void;
}
export function HireCandidateModal({
  candidate,
  isOpen,
  onClose,
  onConfirm
}: HireCandidateModalProps) {
  const {
    addToast
  } = useToast();
  const today = new Date().toISOString().split('T')[0];
  const defaultStart = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().split('T')[0];
  })();
  const [startDate, setStartDate] = useState(defaultStart);
  const [decisionDate, setDecisionDate] = useState(today);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen && candidate) {
      setStartDate(candidate.desiredStartDate || defaultStart);
      setDecisionDate(candidate.offerDecisionDate || today);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);
  if (!candidate) return null;
  const handleConfirm = () => {
    if (!startDate) {
      setError('Start date is required');
      return;
    }
    if (new Date(startDate) < new Date(decisionDate)) {
      setError('Start date cannot be before the offer decision date');
      return;
    }
    onConfirm(candidate, {
      startDate,
      offerDecisionDate: decisionDate
    });
    addToast({
      type: 'success',
      title: 'Candidate Hired!',
      message: `${candidate.firstName} ${candidate.lastName} starts on ${new Date(startDate).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })}.`
    });
    onClose();
  };
  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm bg-white';
  return <Modal isOpen={isOpen} onClose={onClose} title="Hire Candidate" maxWidth="md" data-id="element-912">
      <div className="space-y-5" data-id="element-913">
        <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 border border-green-100" data-id="element-914">
          <div className="h-10 w-10 rounded-full bg-white border border-green-100 flex items-center justify-center text-green-600" data-id="element-915">
            <CheckCircle2 className="h-5 w-5" data-id="element-916" />
          </div>
          <div className="min-w-0" data-id="element-917">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500" data-id="element-918">
              Hiring
            </p>
            <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-919">
              {candidate.firstName} {candidate.lastName}
            </p>
            <p className="text-xs text-gray-500 truncate" data-id="element-920">
              {candidate.role} • {candidate.department}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-921">
          <div data-id="element-922">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-923">
              Offer decision date
            </label>
            <input type="date" value={decisionDate} onChange={e => setDecisionDate(e.target.value)} className={inputClass} data-id="element-924" />
            <p className="text-[11px] text-gray-500 mt-1" data-id="element-925">
              When the offer was accepted.
            </p>
          </div>
          <div data-id="element-926">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-927">
              <span className="inline-flex items-center gap-1.5" data-id="element-928">
                <CalendarPlus className="h-3.5 w-3.5 text-gray-400" data-id="element-929" />
                Start date
              </span>
              <span className="text-red-500 ml-0.5" data-id="element-930">*</span>
            </label>
            <input type="date" value={startDate} min={decisionDate} onChange={e => {
            setStartDate(e.target.value);
            if (error) setError(null);
          }} className={`${inputClass} ${error ? 'border-red-300' : ''}`} data-id="element-931" />
            <p className="text-[11px] text-gray-500 mt-1" data-id="element-932">
              First day on the job.
            </p>
          </div>
        </div>

        {error && <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-id="element-933">
            {error}
          </div>}

        <div className="flex gap-3 pt-2" data-id="element-934">
          <Button variant="ghost" onClick={onClose} className="flex-1 text-gray-700 border border-gray-200 hover:bg-gray-50" data-id="element-935">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} className="flex-1 bg-green-600 hover:bg-green-700" leftIcon={<CheckCircle2 className="h-4 w-4" data-id="element-937" />} data-id="element-936">
            Confirm Hire
          </Button>
        </div>
      </div>
    </Modal>;
}